import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from '@xstate/store/react';
import debounce from 'lodash/debounce';
import { HTMLAttributes, forwardRef, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMount, useUnmount } from 'react-use';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { Form, VStack, tv } from '@eluve/components';
import { Box } from '@eluve/components';

import { BlockDetailsForm } from './BlockDetailsForm';
import { TreeItemHeader } from './TreeItemHeader';
import { GroupBlockControl } from './blocks/GroupBlockControl';
import { templateBuilderStore } from './templateBuilderStore';
import { FlattenedBlock } from './utilities';

const DEBOUNCE_MS = 500;

const blockDetailsFormSchema = z
  .object({
    label: z.string().min(1),
    description: z.string().optional(),
    isRequired: z.boolean(),
    isAiEnabled: z.boolean(),
    isEmailEnabled: z.boolean().optional(),
    placeholderText: z.string().optional(),
    checkboxOptions: z
      .array(z.string().min(1, 'Must be at least one character'))
      .optional(),
  })
  .refine(
    (data) => {
      // If block is required OR AI is disabled, description is required
      if ((data.isRequired || !data.isAiEnabled) && !data.description?.trim()) {
        return false;
      }
      return true;
    },
    {
      message:
        'Description is required when block is required or AI is disabled',
      path: ['description'],
    },
  )
  .refine(
    (data) => {
      // If block is required. description is required
      if (data.isRequired && !data.description?.trim()) {
        return false;
      }
      return true;
    },
    {
      message:
        'Please provide a description when the block is marked as required',
      path: ['isRequired'],
    },
  );

export type BlockDetailsFormData = z.infer<typeof blockDetailsFormSchema>;

const treeItemStyles = tv({
  slots: {
    wrapper: 'w-full list-none',
    treeItem: 'rounded-lg border p-4',
    blockStyles: '',
  },
  variants: {
    collapsed: {
      true: {
        blockStyles: 'hidden',
      },
    },
    ghost: {
      true: {
        treeItem: 'opacity-50 [&>*]:bg-transparent',
      },
    },
    clone: {
      true: {
        wrapper: 'pointer-events-none inline-block pt-2',
        treeItem: 'rounded-lg py-2 pr-6 shadow-lg',
      },
    },
  },
});

export interface Props extends Omit<HTMLAttributes<HTMLLIElement>, 'id'> {
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  depth: number;
  disableInteraction?: boolean;
  disableSelection?: boolean;
  ghost?: boolean;
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
  indicator?: boolean;
  indentationWidth: number;
  value: FlattenedBlock;
  wrapperRef?(node: HTMLLIElement): void;
}

export const TreeItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      clone,
      depth,
      ghost,
      handleProps,
      indentationWidth,
      collapsed = false,
      style,
      value,
      wrapperRef,
      ...props
    },
    ref,
  ) => {
    const { treeItem, wrapper, blockStyles } = treeItemStyles({
      ghost,
      clone,
      collapsed,
    });

    const isReadonly = useSelector(
      templateBuilderStore,
      (store) => store.context.isReadOnly,
    );

    const isImported = useSelector(
      templateBuilderStore,
      (store) => store.context.isImported,
    );

    const form = useForm<BlockDetailsFormData>({
      resolver: zodResolver(blockDetailsFormSchema),
      mode: 'all',
      values: {
        label: value.label,
        description: value.description,
        isRequired: value.isRequired ?? false,
        isAiEnabled: value.isAiEnabled ?? true,
        isEmailEnabled: value.isEmailEnabled ?? false,
        checkboxOptions:
          value.type === 'checkbox'
            ? value.options.map((o) => o.label)
            : undefined,
        placeholderText:
          value.type === 'text' ? value.placeholderText : undefined,
      },
    });

    const saveBlockDetails = useCallback(
      (formData: BlockDetailsFormData) => {
        templateBuilderStore.send({
          type: 'setBlockDetails',
          id: value.id,
          label: formData.label,
          description: formData.description,
          isRequired: formData.isRequired,
          checkboxOptions: formData.checkboxOptions,
          isAiEnabled: formData.isAiEnabled,
          isEmailEnabled: formData.isEmailEnabled,
          placeholderText: formData.placeholderText,
        });
      },
      [value.id],
    );

    const setBlockInvalid = useCallback(() => {
      templateBuilderStore.send({
        type: 'setBlockInvalid',
        id: value.id,
      });
    }, [value.id]);

    const debouncedSave = useMemo(
      () =>
        debounce(() => {
          if (!isReadonly) {
            const isValid = form.formState.isValid;
            if (isValid) {
              const data = form.getValues();
              saveBlockDetails(data);
            } else {
              setBlockInvalid();
            }
          }
        }, DEBOUNCE_MS),
      [form, saveBlockDetails, setBlockInvalid, isReadonly],
    );

    const handleFormChange = useCallback(() => {
      debouncedSave();
    }, [debouncedSave]);

    useMount(() => {
      form.trigger();
    });

    useUnmount(() => {
      debouncedSave.cancel();
    });

    return (
      <Form {...form}>
        <form className="w-full" onChange={handleFormChange}>
          <li
            ref={wrapperRef}
            className={wrapper()}
            style={{
              paddingLeft: `${indentationWidth * depth}px`,
            }}
            {...props}
          >
            <Box
              ref={ref}
              className={treeItem({
                className:
                  'overflow-hidden p-0 focus-within:border-border-tertiary',
              })}
              style={{
                ...style,
                boxShadow:
                  '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              }}
            >
              <TreeItemHeader
                handleProps={handleProps}
                collapsed={collapsed}
                block={value}
              />
              <VStack gap={6} className={blockStyles({ className: 'p-5' })}>
                <BlockDetailsForm onSubmit={handleFormChange} block={value} />
                {match(value)
                  .with({ type: 'group' }, (group) =>
                    isReadonly || isImported ? null : (
                      <GroupBlockControl
                        parentId={group.parentId}
                        group={group}
                      />
                    ),
                  )
                  .otherwise(() => null)}
              </VStack>
            </Box>
          </li>
        </form>
      </Form>
    );
  },
);
